import { closeCurrentTag } from '@/utils/tags';
import { handleDatabaseRequestError } from '@/utils/error';
import { objectsAreEqual, deepClone, array_concat } from '@/utils';

export default {
  props: {
    autoGeneratedId: {
      default: true,
      type: Boolean
    },
    definition: {
      default() {
        return {};
      },
      type: Object
    },
    disabled: {
      default: false,
      type: Boolean
    },
    id: {
      type: Number,
      default: 0
    },
    fetchById: {
      default: id => {
        throw new Error('fetchById is not provided as a prop');
      },
      type: Function
    },
    preCreateFetch: {
      default: () => Promise.resolve(),
      type: Function
    },
    isEdit: {
      default: false,
      type: Boolean
    },
    itemType: {
      default: '',
      type: String
    },
    returnTo: {
      default: '',
      type: String
    },
    save: {
      default: data => {
        throw new Error('save is not provided as a prop');
      },
      type: Function
    },
    pageLoading: {
      type: Boolean,
      default: false
    },
    isEmbedded: {
      type: Boolean,
      default: false
    },
    forceValues: {
      type: Object,
      default: () => ({})
    },
    saveButtonPosition: {
      type: String,
      default: 'top'
    },
    preventDefault: {
      type: Boolean,
      default: false
    },
    getDependencies: {
      type: Function,
      default: () => []
    },
    promptRouteLeave: Function,
    injectFormValues: {
      type: Object, // When object is changed, the values of injectFormValues are copied into form, changing the fields' values
      default: () => ({})
    },
    getTagTitle: Function,
    getLayout: Function
  },
  data() {
    const fieldsDefinition = this.definition;
    const fieldNames = Object.keys(fieldsDefinition);
    this.defaultForm = Object.assign(
      {},
      fieldNames.reduce((form, name) => {
        form[name] = fieldsDefinition[name].defaultValue;
        return form;
      }, {})
    );

    const fields = {};
    fieldNames.reduce((target, name) => {
      const field = fieldsDefinition[name];
      if (field.type) {
        target[name] = field;
      }
      return target;
    }, fields);

    return {
      errorMessage: '',
      formItemStyle: {},
      errors: {},
      formData: {},
      form: Object.assign({}, this.defaultForm),
      actionLoading: false,
      tempRoute: {},
      userListOptions: [],
      formLabelPosition: 'right',
      forceExit: false,
      initvals: {}
    };
  },
  computed: {
    fields() {
      const fieldsDefinition = this.definition;
      const fieldNames = Object.keys(fieldsDefinition);

      const fields = {};
      fieldNames.reduce((target, name) => {
        const field = fieldsDefinition[name];
        if (field.type) {
          target[name] = field;
        }
        return target;
      }, fields);

      return fields;
    },
    columnsFields() {
      if (this.getLayout) {
        const layout = this.getLayout(this);
        const columnsProps = this.generateColumnsPropertiesFromPageLayout(layout);
        return columnsProps.columnFields;
      }
      return this.splitFieldsInHalfAsArray(this.fields);
    },
    columnsWidthPercentages() {
      if (this.getLayout) {
        const layout = this.getLayout(this);
        const columnsProps = this.generateColumnsPropertiesFromPageLayout(layout);
        return columnsProps.widths;
      }
      return [];
    },
    lang() {
      return this.$store.getters.language;
    },
    validationErrorInputPrefix() {
      return this.$store.getters['validationErrorInputPrefix'];
    }
  },
  watch: {
    forceValues(val, oldVal) {
      if (objectsAreEqual(val, oldVal)) {
        this.refreshFormValues();
      }
    },
    definition(definition) {
      this.refreshFormValues();
    },
    id(val) {
      if (this.isEdit) {
        const id = this.id || (this.$route.params && this.$route.params.id);
        this.fetchData(id);
      }
    },
    form: {
      deep: true,
      handler: function(val) {
        this.$store.dispatch('editor/updateForm', val);
        const flatFields = this.getFlatFields(this.fields);
        const changedKeys = Object.keys(val).filter(key => val[key] !== (this.formSnapshot && this.formSnapshot[key]));
        Object.keys(flatFields).forEach(name => {
          if (flatFields[name].valueOnFormChange) {
            const newVal = flatFields[name].valueOnFormChange(val, this.form[name], changedKeys);
            const oldVal = this.form[name];
            let valueIsChanged = false;
            if (Array.isArray(newVal) && typeof oldVal === 'object') {
              valueIsChanged = this.formObjectsAreDifferent(newVal, Object.values(oldVal));
            } else {
              valueIsChanged = newVal !== oldVal;
            }
            if (valueIsChanged) {
              this.$set(this.form, name, newVal);
            }
          }
        });
        this.formSnapshot = Object.assign({}, val);
      }
    },
    injectFormValues(injectFormValues) {
      this.form = Object.assign({}, this.form, injectFormValues);
    }
  },
  created() {
    window.addEventListener('beforeunload', this.beforeUnload, false);
    if (this.isEdit) {
      const id = this.id || (this.$route.params && this.$route.params.id);
      this.fetchData(id).then(data => {
        const dependencies = this.getDependencies(data);
        dependencies.forEach(dep => {
          const params = dep.params || {};
          if (dep.inactiveID) {
            params.query = {
              includeID: dep.inactiveID
            };
          }
          this.$store.dispatch(dep.entity + '/getItems', params);
        });
      });
    } else {
      this.fetchDependencies();
      this.leavePageOnReject(this.preCreateFetch());
    }
    this.refreshFormValues();
    // Why need to make a copy of this.$route here?
    // Because if you enter this page and quickly switch tag, may be in the execution of the setTagsViewTitle function,
    // this.$route is no longer pointing to the current page
    // https://github.com/PanJiaChen/vue-element-admin/issues/1221
    this.tempRoute = Object.assign({}, this.$route);
    this.$store.dispatch('notify/disableErrorCodePrefix', this.validationErrorInputPrefix);
  },
  destroyed() {
    window.removeEventListener('beforeunload', this.beforeUnload);
    window.removeEventListener('resize', this.handleResize);
    this.$store.dispatch('notify/enableErrorCodePrefix', this.validationErrorInputPrefix);
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
    if (!this.isEdit) {
      this.setFocus();
    }
  },
  methods: {
    getRules(fields) {
      return Object.keys(fields).reduce((rules, key) => {
        const staticRules = fields[key].rules;
        const dynamicRules = fields[key].dynamicRules && fields[key].dynamicRules(this.form);
        rules[key] = array_concat(staticRules || [], dynamicRules || []);
        return rules;
      }, {});
    },
    getWidthFromPercentage(percentage, columns) {
      if (percentage === undefined) {
        return 24 / (this.isEmbedded ? 1 : columns.length);
      }
      return Math.floor(0.01 * percentage * 24);
    },
    generateColumnsPropertiesFromPageLayout(layout, tabIndex) {
      const { named, sections } = layout;
      const columns = [];
      const columnWidths = [];
      const flatFields = this.getFlatFields();
      if (named) {
        sections.forEach(({ fields, width }) => {
          const columnFields = fields.reduce((column, fieldName) => {
            column[fieldName] = flatFields[fieldName];
            return column;
          }, {});
          columns.push(columnFields);
          columnWidths.push(width);
        });
      } else {
        const fieldsOnThisPage = tabIndex === undefined ? flatFields : this.fields[tabIndex];
        const fieldNamesStack = Object.keys(fieldsOnThisPage);
        sections.forEach(({ capacity, width }) => {
          if (!fieldNamesStack.length) {
            return;
          }
          let columnFields = {};
          if (capacity === undefined) {
            columnFields = fieldNamesStack.reduce((column, fieldName) => {
              column[fieldName] = flatFields[fieldName];
              return column;
            }, {});
          } else {
            for (let i = 0; i < capacity && fieldNamesStack.length; i++) {
              const fieldName = fieldNamesStack.shift();
              columnFields[fieldName] = flatFields[fieldName];
            }
          }
          columns.push(columnFields);
          columnWidths.push(width);
        });
      }
      return { columnFields: columns, widths: columnWidths };
    },
    getLabel(spec, name) {
      const specs = this.getProps(spec);
      return (specs.caption && this.$i18n.t(`${specs.caption}`)) || this.$i18n.t(`common.${name}`);
    },
    getDisabledProp(spec) {
      return this.disabled || !!this.getStaticProps(spec).disabled || this.getDynamicProps(spec).disabled;
    },
    getProps(spec) {
      return { ...this.getStaticProps(spec), ...this.getDynamicProps(spec) };
    },
    getStaticProps(spec) {
      return spec.props || {};
    },
    getDynamicProps(spec) {
      return (spec.dynamicProps && spec.dynamicProps(this.form)) || {};
    },
    setFocus() {
      this.$nextTick(() => {
        const firstComponent = this.getFirstFocusableInputField();
        if (firstComponent) {
          firstComponent.focus();
        }
      });
    },
    createRef(grandparent, parent, child) {
      let me = 'inputField' + grandparent.toString().padStart(4, '0');
      if (parent !== undefined) {
        me += parent.toString().padStart(4, '0');
      }
      if (child !== undefined) {
        me += child.toString().padStart(4, '0');
      }
      return me;
    },
    getFirstFocusableInputField() {
      const inputFieldRefKeys = Object.keys(this.$refs)
        .filter(key => key.startsWith('inputField'))
        .sort();
      let key;
      while ((key = inputFieldRefKeys.shift())) {
        const component = this.$refs[key][0];
        if (!component.disabled && !component.$attrs.disabled) {
          return component;
        }
      }
      return undefined;
    },
    leavePageOnReject(promise) {
      const pageVersionBeforeFetch = this.$store.getters['pageVersion'];
      return promise.catch(error => {
        const pageVersion = this.$store.getters['pageVersion'];
        if (error && error.response && pageVersion === pageVersionBeforeFetch) {
          const path = this.mapToPath(error.response.status);
          if (path) {
            this.forceRouteLeave = true;
            this.$router.replace({ path: path });
          }
        }
      });
    },
    getFlatFields() {
      let flatFields = this.fields;

      if (Array.isArray(flatFields)) {
        flatFields = flatFields.reduce((acc, tabFields) => {
          acc = { ...acc, ...tabFields };
          return acc;
        }, {});
      }
      return flatFields;
    },
    formHasChanged() {
      const userEditableForm = {};
      const userEditableFormData = {};

      const flatFields = this.getFlatFields(this.fields);

      Object.keys(flatFields).forEach(key => {
        userEditableForm[key] = this.form[key];
        userEditableFormData[key] = this.formData[key];
      });
      const areNotEqual = JSON.stringify(userEditableForm) !== JSON.stringify(userEditableFormData);
      if (areNotEqual) {
        console.log(JSON.stringify(userEditableForm), JSON.stringify(userEditableFormData));
      }
      return areNotEqual;
    },
    clearInputErrorFor(name, additionalFields) {
      delete this.errors[name.toLowerCase()];
      if (Array.isArray(additionalFields)) {
        additionalFields.forEach(field => {
          delete this.errors[field.toLowerCase()];
        });
      }
    },
    getFirstInputError(errors, name, additionalFields) {
      if (!Array.isArray(additionalFields)) {
        additionalFields = [];
      }
      return (
        errors[name.toLowerCase()] ||
        additionalFields.map(field => errors[field.toLowerCase()]).filter(error => error)[0]
      );
    },
    doPromptRouteLeave() {
      if (this.forceRouteLeave) {
        return Promise.resolve();
      }
      if (this.promptRouteLeave) {
        return this.promptRouteLeave();
      }
      return this.$store.dispatch('notify/confirm', {
        title: this.$i18n.t('common.warning'),
        message: this.$i18n.t('common.leave_unsaved_warning'),
        confirmButtonText: this.$i18n.t('common.save'),
        cancelButtonText: this.$i18n.t('common.dontSave')
      });
    },
    handleRouteLeave(to, from, next) {
      if (this.formHasChanged() && !this.forceExit) {
        const saveErrorMsg = this.$i18n.t('error.save');
        next(false);
        this.doPromptRouteLeave()
          .then(async () => {
            try {
              const done = await this.persistForm();
              if (done) {
                next();
              }
            } catch (err) {
              this.$store.dispatch('toast', { message: saveErrorMsg });
              console.log(err);
            }
          })
          .catch(action => {
            if (action === 'cancel') {
              next();
            } else {
              next(false);
            }
          });
      } else {
        next();
      }
    },
    beforeUnload(e) {
      if (this.formHasChanged()) {
        // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    },
    fetchDependencies(dep) {
      const dependencies = this.getDependencies();
      dependencies.forEach(dep => {
        const params = dep.params || {};
        this.$store.dispatch(dep.entity + '/getItems', params);
      });
    },
    handleResize() {
      const { width } = this.$el.getBoundingClientRect();
      if (width <= 767) {
        this.formLabelPosition = 'top';
      } else {
        this.formLabelPosition = 'right';
      }
    },
    splitFieldsInHalfAsArray(fields) {
      const keys = Object.keys(fields);
      if (keys.length < 4) {
        const columns = [];
        columns.push(fields);
        return columns;
      }
      let columns = [];
      columns = keys.reduce((acc, key, index) => {
        const columnIndex = index < keys.length / 2 ? 0 : 1;
        if (!acc[columnIndex]) {
          acc[columnIndex] = {};
        }
        acc[columnIndex][key] = fields[key];
        return acc;
      }, columns);
      return columns;
    },
    handleInit(name, init) {
      this.formData[name] = init;
    },
    clearForm() {
      this.formData = {};
      this.refreshFormValues();
      this.$refs.form.clearValidate();
    },
    refreshFormValues() {
      // this.form cannot be Computed because it is passed as v-model for input components
      const fieldsDefinition = this.definition;
      const fieldNames = Object.keys(fieldsDefinition);
      const defaultForm = Object.assign(
        {},
        fieldNames.reduce((form, name) => {
          form[name] = fieldsDefinition[name].defaultValue;
          return form;
        }, {})
      );
      this.form = Object.assign({}, defaultForm, this.formData, this.forceValues);
      this.formData = deepClone(this.form);
    },
    fetchData(id) {
      this.$store.dispatch('notify/disableErrorHTTPCode', 403);
      this.$store.dispatch('notify/disableErrorHTTPCode', 404);
      return this.leavePageOnReject(
        this.fetchById(id)
          .then(data => {
            this.formData = Object.assign(this.formData, data);
            this.initvals = Object.assign({}, this.formData, data);
            this.$set(this.formData, 'id', data.id);
            this.refreshFormValues();
            this.refreshTagTitle(data);
            this.$refs.form.clearValidate();
            this.setFocus();
            this.$emit('fetched');
            return data;
          })
          .finally(_ => {
            this.$store.dispatch('notify/enableErrorHTTPCode', 403);
            this.$store.dispatch('notify/enableErrorHTTPCode', 404);
          })
      );
    },
    refreshTagTitle(item) {
      if (this.isEdit) {
        const newTag = this.getTagTitle ? { title: this.getTagTitle(item) } : { name: item.name || item.code || '' };
        this.setTagsViewTitle(newTag);
      }
    },
    mapToPath(statusCode) {
      switch (statusCode) {
        case 403:
          return '/403';
        case 404:
          return '/404';
        default:
          return null;
      }
    },
    setTagsViewTitle({ name, title }) {
      if (this.isEmbedded) {
        return;
      }
      if (title === undefined) {
        title = this.$i18n.t('route.edit' + this.itemType) + ' ' + (name || '');
      }
      const route = Object.assign({}, this.tempRoute, {
        title: `${title}`
      });
      this.$store.dispatch('updateVisitedView', route);
    },
    async persistForm() {
      this.errorMessage = '';
      this.form = Object.assign(this.form, this.forceValues);
      try {
        await this.$refs.form.validate();
      } catch (err) {
        this.$store.dispatch('notify/formValidationFailed');
        return false;
      }

      if (!this.isEdit && this.autoGeneratedId) {
        // make sure no id is present
        delete this.form.id;
      }
      this.actionLoading = true;
      this.$emit('save');
      this.errors = Object.assign({});
      try {
        const _form = Object.assign({}, this.form);
        const response = await this.save(this.form);
        // This fixes bug that form magically resets after save()
        this.form = Object.assign({}, _form);
        if (!this.preventDefault && (!response || !(typeof response === 'string'))) {
          if (this.isEdit) {
            this.$store.dispatch('notify/dataUpdated');
          } else {
            this.$store.dispatch('notify/dataCreated');
          }
        }
        this.actionLoading = false;
        this.formData = Object.assign(this.formData, this.form, {});
        this.refreshTagTitle(this.form);
        this.$emit('saved', this.form);
        this.$emit('persisted', response);
        return response || true;
      } catch (error) {
        this.errors = handleDatabaseRequestError(this, error, this.preventDefault);
        this.actionLoading = false;
        return false;
      }
    },
    async handleSave() {
      const item = await this.persistForm();
      if (item && !this.isEdit && !this.isEmbedded && !this.preventDefault) {
        closeCurrentTag(
          this,
          {
            name: 'Edit' + this.itemType,
            params: { id: item.id }
          },
          false
        );
      }
      return item;
    },
    async saveAndClose() {
      const done = await this.persistForm();
      if (done && !this.preventDefault) {
        closeCurrentTag(this, false, { name: this.returnTo });
      }
    },
    async saveAndNext() {
      const done = await this.persistForm();
      if (done && !this.preventDefault) {
        const { params } = this.$route;
        const name = 'Create' + this.itemType;

        if (this.isEdit) {
          this.$router.push({ name, params });
        } else {
          let href = this.$router.resolve({ name, params }).href;
          href = href.replace(/^#/g, '');
          this.$router.push({ path: '/redirect' + href });
        }
      }
    },
    formArraysAreDifferent(arr1, arr2) {
      if (arr1.length !== arr2.length) {
        return true;
      }
      const _arr1 = arr1.sort();
      const _arr2 = arr2.sort();
      const isDifferent = _arr1.some((val, index) => {
        if (_arr1[index] && typeof _arr2[index] === typeof {}) {
          return this.formObjectsAreDifferent(_arr1[index], _arr2[index]);
        }
        return _arr1[index] !== _arr2[index];
      });
      return isDifferent;
    },
    formObjectsAreDifferent(obj1, obj2) {
      const isDifferent = Object.keys(obj1).some(key => {
        if (obj1[key] && typeof obj1[key] === typeof {}) {
          if (Array.isArray(obj1[key])) {
            return this.formArraysAreDifferent(obj1[key], obj2[key]);
          } else if (obj2 && obj2[key] !== undefined) {
            return this.formObjectsAreDifferent(obj1[key], obj2[key]);
          }
          return false;
        }
        // Assume null to be same as empty string
        const _val1 = obj1[key] === null ? '' : obj1[key];
        const _val2 = obj2 === null || obj2[key] === null ? '' : obj2[key];
        // Debug code
        // if (_val1 !== _val2) {
        //   console.log('different: ', { key, val1: obj1[key], val2: obj2[key] });
        // }
        return _val1 !== _val2;
      });
      return isDifferent;
    },
    async close() {
      try {
        if (this.formHasChanged()) {
          await this.$store.dispatch('notify/closeUnsavedConfirm');
        }
        if (!this.preventDefault) {
          this.forceExit = true;
          closeCurrentTag(this, false, { name: this.returnTo });
        }
      } catch (err) {
        // do nothing and stay on page
      }
    }
  }
};
